import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ShoppingCart, Receipt, Plus, AlertTriangle, Bike, X, Banknote, RotateCcw } from 'lucide-react'
import Header from '../components/Header'
import CheckoutSheet from '../components/CheckoutSheet'
import NotificationPermissionPrompt from '../components/NotificationPermissionPrompt'
import DeliveryApprovalCountdown from '../components/DeliveryApprovalCountdown'
import OrderChatSheet from '../components/OrderChatSheet'
import DishListPanel from '../components/DishListPanel'
import { showToast } from '../components/Toast'
import { useI18n } from '../i18n/useI18n'
import { useCheckoutStyle } from '../hooks/useCheckoutStyle'
import { useStore, type Order, type Store } from '../store/useStore'
import { useMyOrders } from '../hooks/useMyOrders'
import { useDeliveryDetails } from '../hooks/useDeliveryDetails'
import { useChatUnreadNotifier } from '../hooks/useChatUnreadNotifier'
import { isSelfOrderEnabled } from '../lib/features'
import { api } from '../services/api'
import { MessageCircle } from 'lucide-react'
import { displayOrderNumber } from '../lib/orderNumber'
import PickupPassCard from '../components/PickupPassCard'
import { itemNameLines, itemNameOneLine } from '../lib/itemName'
import VariationLine from '../components/VariationLine'

interface OpenBill {
  storeId: string
  storeName: string
  orders: Order[]
  total: number
}

// #1535 卓伝票の状態グループ。未提供 (受付/調理中) を上に置き「まだ来ていない
// もの」をひと目で数えられるようにする (CheckoutSheet と同じ並び・配色)。
const SLIP_STATUS_ORDER = ['pending_approval', 'received', 'preparing', 'served']
const SLIP_STATUS_COLOR: Record<string, string> = {
  pending_approval: 'bg-purple-100 text-purple-700',
  received: 'bg-amber-100 text-amber-700',
  preparing: 'bg-blue-100 text-blue-700',
  served: 'bg-emerald-100 text-emerald-700',
}

// #1535 注文時刻 (HH:MM)。「頼んだ/頼んでない」の揉め防止用の記録。
function formatOrderTime(iso: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

// #1535 店側起票 (合成持ち主) 判定 — 付け替え前の放題プラン伝票も「店員が入力」側
function isStaffEnteredOrder(o: Order): boolean {
  return o.placedBy === 'staff' || /^(staff_|table_)/.test(o.customerId || '')
}

/**
 * Customer-facing active bill (#289 deferred-payment model).
 *
 * Lifecycle-scoped: only shows the customer's OPEN session at a store
 * (unpaid + not cancelled). Once the bill is paid, those orders move to
 * /history (the transaction record) and this page goes back to empty —
 * the page has no long-term history value of its own.
 *
 * Closed orders (paid or cancelled) are reachable via /history via the
 * transaction that confirm_checkout wrote.
 */
export default function MyOrders() {
  const navigate = useNavigate()
  const { t } = useI18n()
  const { stores, userId, reorderToCart } = useStore()
  const { orders: rawOrders, tableOrders, loading } = useMyOrders()
  // #1025 届け先は公開 doc から分離したので onSnapshot には乗ってこない。
  // ここで注入し、以降の描画は今までどおり order.deliveryAddress を読む。
  const orders = useDeliveryDetails(rawOrders, 'customer', userId)
  const [checkoutTarget, setCheckoutTarget] = useState<OpenBill | null>(null)
  // #1535 卓全体の合計 = 自分のこの卓の分 + 同卓のほか (友達・店員入力)。
  // シートを開く前に「卓としていくらか」が分かるようにする (会計時に金額が
  // 跳ねて揉めるのを防ぐ)。会計対象の定義はサーバの table_chargeable_orders
  // に揃える (キャンセル・承認待ち・出前を除く)。
  const tableAllTotal = useMemo(() => {
    if (!tableOrders.length) return 0
    const sid = tableOrders[0].storeId
    const tn = tableOrders[0].tableNumber
    const chargeable = (o: Order) =>
      !o.paid && o.status !== 'cancelled' && o.status !== 'pending_approval'
      && o.orderType !== 'delivery'
    const ownAtTable = orders.filter(
      (o) => o.storeId === sid && o.tableNumber === tn && chargeable(o),
    )
    return [...ownAtTable, ...tableOrders.filter(chargeable)]
      .reduce((n, o) => n + o.totalAmount, 0)
  }, [orders, tableOrders])
  const [reportTarget, setReportTarget] = useState<Order | null>(null)
  const [reporting, setReporting] = useState(false)
  const [cancelTarget, setCancelTarget] = useState<Order | null>(null)
  const [cancelling, setCancelling] = useState(false)
  // #338 in-order chat — which order's chat sheet is open (null = closed).
  const [chatOrderId, setChatOrderId] = useState<string | null>(null)

  // Fire browser notifications + update tab title when new messages arrive
  // on any of the user's orders. activeOrderId excludes the currently-open
  // sheet so we don't re-notify for messages the user is already reading.
  useChatUnreadNotifier(orders, 'customer', chatOrderId)

  const chatOrder = chatOrderId ? orders.find((o) => o.id === chatOrderId) || null : null

  const submitMissingReport = async () => {
    if (!reportTarget || !userId || reporting) return
    setReporting(true)
    try {
      const res = await api.reportOrderMissing(reportTarget.id, userId)
      if (res?.detail) {
        showToast({ type: 'error', message: res.detail })
        return
      }
      showToast({ type: 'success', message: t('myOrders.reportedToast') })
      setReportTarget(null)
    } catch {
      showToast({ type: 'error', message: t('myOrders.reportFailed') })
    } finally {
      setReporting(false)
    }
  }

  const submitCancel = async () => {
    if (!cancelTarget || !userId || cancelling) return
    setCancelling(true)
    try {
      const res = await api.cancelMyOrder(cancelTarget.id, userId)
      if (res?.detail) {
        showToast({ type: 'error', message: res.detail })
        return
      }
      showToast({ type: 'success', message: t('myOrders.cancelledToast') })
      setCancelTarget(null)
    } catch {
      showToast({ type: 'error', message: t('myOrders.cancelFailed') })
    } finally {
      setCancelling(false)
    }
  }

  // Open bills = unpaid AND not cancelled, grouped by store. Delivery
  // orders are pre-paid so they never land here — they have their own
  // section below.
  // #1213 カウンター受け取り (takeout) も先払いチャネルなので卓の伝票には出さない。
  // 決済前に落ちた注文が「卓のお会計」に混ざると二重に払わせる導線になる。
  const openBills = useMemo<OpenBill[]>(() => {
    const map = new Map<string, OpenBill>()
    for (const o of orders) {
      if (o.paid || o.status === 'cancelled') continue
      if (o.orderType === 'delivery' || o.orderType === 'takeout') continue
      const storeName = stores.find((s) => s.id === o.storeId)?.name || o.storeName || o.storeId
      const existing = map.get(o.storeId)
      if (existing) {
        existing.orders.push(o)
        existing.total += o.totalAmount
      } else {
        map.set(o.storeId, { storeId: o.storeId, storeName, orders: [o], total: o.totalAmount })
      }
    }
    return Array.from(map.values())
  }, [orders, stores])

  // #2344 会計シートを開いている間にも、店は品を取り消したり会計を確定したりする。
  // 開いた時点の伝票を握り続けると、お客様の画面には古い金額が出たままになる
  // (体験で確かめたときは「¥4,200」を出し続けたまま、実際の会計は ¥2,800 だった)。
  // 伝票は毎回 openBills から引き直す。
  //
  // 伝票が消える理由は 2 つある: **払い終えた**か、店が全部取り消したか。
  // 「終わりました」と言ってよいのは前者だけなので、元の注文が paid になったかで
  // 区別する (取り消されただけの客に「お支払いが確認されました」と出さない)。
  // **閉じる判断はシート側に残す** — カード決済の確定中に外から畳むと、
  // レシートの画面へ進めなくなる。
  const liveCheckoutTarget = useMemo<{ bill: OpenBill; settled: boolean } | null>(() => {
    if (!checkoutTarget) return null
    const live = openBills.find((b) => b.storeId === checkoutTarget.storeId)
    if (live) return { bill: live, settled: false }
    const wasInBill = new Set(checkoutTarget.orders.map((o) => o.id))
    const settled = orders.some((o) => wasInBill.has(o.id) && o.paid)
    return { bill: { ...checkoutTarget, orders: [], total: 0 }, settled }
  }, [checkoutTarget, openBills, orders])

  // #338 post-delivery UX: delivery orders stay in this "active" list
  // for 30 minutes after delivered/cancelled so the customer can still
  // file "届いていない" or chat the driver about last-minute details.
  // Beyond 30 minutes the card disappears from here; access is via
  // /history which carries a short-lived "注文詳細を見る" link back
  // to this page for the same 30-minute window.
  // missingReported orders stay visible regardless of age so the open
  // dispute is always reachable.
  const GRACE_ACTIVE_MS = 30 * 60 * 1000
  const isTerminal = (o: Order): boolean =>
    o.status === 'delivered' || o.status === 'picked_up' || o.status === 'cancelled'
  const msSinceUpdate = (o: Order): number => {
    const ts = o.updatedAt || o.createdAt
    if (!ts) return 0
    const n = new Date(ts).getTime()
    if (Number.isNaN(n)) return 0
    return Date.now() - n
  }

  // #1213 出前とカウンター受け取りは「卓の伝票」に属さない先払い注文なので、
  // 卓ごとの openBills とは別枠でパイプラインを表示する。
  const deliveryActive = useMemo<Order[]>(() => {
    return orders.filter((o) => {
      if (o.orderType !== 'delivery' && o.orderType !== 'takeout') return false
      // #1217 受け取り済みは即座に一覧から外す。カウンター受け取りは受け取った
      // 時点で完結し、出前のような「届いていない」申告の猶予が要らない
      // (履歴は /history で見られる)。
      if (o.orderType === 'takeout' && o.status === 'picked_up') return false
      if (!isTerminal(o)) return true              // in-flight pipeline
      if (o.missingReported) return true           // open dispute
      return msSinceUpdate(o) < GRACE_ACTIVE_MS    // within 30min grace
    })
  }, [orders])

  if (!isSelfOrderEnabled()) {
    navigate('/home', { replace: true })
    return null
  }

  const statusBadge = (order: Order) => {
    const colorMap: Record<Order['status'], string> = {
      pending_approval: 'bg-purple-100 text-purple-700',
      received: 'bg-amber-100 text-amber-700',
      preparing: 'bg-blue-100 text-blue-700',
      served: 'bg-emerald-100 text-emerald-700',
      // #1213 カウンター受け取り: ready = 受け取り可能 (呼び出し中) / picked_up = 受領済み
      ready: 'bg-matcha/15 text-matcha',
      picked_up: 'bg-emerald-100 text-emerald-700',
      out_for_delivery: 'bg-indigo-100 text-indigo-700',
      delivered: 'bg-emerald-100 text-emerald-700',
      cancelled: 'bg-gray-200 text-text-secondary',
    }
    return (
      <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-body font-semibold ${colorMap[order.status]}`}>
        {t(`myOrders.status.${order.status}`)}
      </span>
    )
  }

  // #968 再注文: 着席中（このページに開いている＝同店舗で会計前）に、同じ品を
  // もう一度カートへ。店舗メニューに移動してそのまま注文できる。
  const handleReorder = (bill: OpenBill, order: Order) => {
    const store = stores.find((s) => s.id === bill.storeId)
      || ({ id: bill.storeId, name: bill.storeName, category: '' } as unknown as Store)
    reorderToCart(store, order.items, order.tableNumber)
    showToast({ type: 'success', message: t('myOrders.reorderedToast') })
    navigate(`/stores/${bill.storeId}`)
  }

  const totalOrderCount = openBills.reduce((n, b) => n + b.orders.length, 0)

  return (
    <div className="min-h-screen bg-warm-white safe-top safe-bottom pb-10">
      <Header
        title={t('myOrders.title')}
        onBack={() => navigate(-1)}
        onHome={() => navigate('/home')}
      />
      <NotificationPermissionPrompt />

      <div className="px-5 pt-5 space-y-5">
        {loading && openBills.length === 0 && (
          <div className="py-10 text-center text-text-tertiary font-body text-sm">{t('common.loading')}</div>
        )}
        {!loading && openBills.length === 0 && deliveryActive.length === 0 && (
          <div className="flex flex-col items-center justify-center py-14 bg-gray-50 rounded-xl">
            <ShoppingCart size={28} className="text-text-tertiary mb-2" />
            <p className="text-text-secondary font-body text-sm">{t('myOrders.emptyOpen')}</p>
            <p className="text-text-tertiary font-body text-xs mt-1">{t('myOrders.historyHint')}</p>
            <button
              onClick={() => navigate('/history')}
              className="mt-4 text-xs text-yokoso-red font-body font-semibold underline"
            >
              {t('myOrders.seeHistory')}
            </button>
          </div>
        )}

        {/* #284 delivery in-progress section.
         * Uber-Eats-style step timeline + self-cancel (received only) +
         * missing-report (after dispatch, within 30min grace). Cards age
         * out of this section 30 minutes after delivered/cancelled. */}
        {deliveryActive.map((order) => {
          const storeName = stores.find((s) => s.id === order.storeId)?.name || order.storeName || order.storeId
          // #1213 カウンター受け取りは配達の代わりに「受け取り可能 → 受け取り済み」
          const steps: { key: Order['status']; label: string }[] =
            order.orderType === 'takeout'
              ? [
                  { key: 'received', label: t('myOrders.stepReceived') },
                  { key: 'preparing', label: t('myOrders.stepPreparing') },
                  { key: 'ready', label: t('myOrders.stepReady') },
                  { key: 'picked_up', label: t('myOrders.stepPickedUp') },
                ]
              : [
                  { key: 'received', label: t('myOrders.stepReceived') },
                  { key: 'preparing', label: t('myOrders.stepPreparing') },
                  { key: 'out_for_delivery', label: t('myOrders.stepOutForDelivery') },
                  { key: 'delivered', label: t('myOrders.stepDelivered') },
                ]
          const currentIdx = steps.findIndex((s) => s.key === order.status)
          const isCancelled = order.status === 'cancelled'
          const canCancel = order.status === 'received' && !order.missingReported && !isCancelled
          const canReportMissing =
            (order.status === 'out_for_delivery' || order.status === 'delivered')
            && !order.missingReported
          return (
            <div key={order.id} className={`rounded-2xl bg-white shadow-card overflow-hidden ${
              isCancelled ? 'border border-gray-300 opacity-70'
              : order.missingReported ? 'border border-red-300'
              : ''
            }`}>
              <div className={`px-4 py-3 border-b flex items-center gap-2 ${
                isCancelled ? 'bg-gray-100 border-gray-200' : 'bg-matcha/10 border-matcha/20'
              }`}>
                <Bike size={16} className={isCancelled ? 'text-text-tertiary' : 'text-matcha'} />
                <p className="font-display text-sm font-semibold text-text-primary flex-1 truncate">
                  {storeName}
                </p>
                <span className="font-display text-xs font-semibold text-text-secondary">
                  #{displayOrderNumber(order)}
                </span>
              </div>

              {/* #1215 店頭受け取り: 番号 + 受け取り QR。カウンターで提示する。
                  受け渡し済み (picked_up) / キャンセル後は出さない。 */}
              {order.orderType === 'takeout' && !isCancelled && order.status !== 'picked_up'
 && (
                <PickupPassCard order={order} />
              )}

              {/* Cancelled: skip the step timeline and show a clear
               * cancellation notice with who cancelled + reason + refund
               * confirmation. Active: show the step timeline. */}
              {isCancelled ? (
                <div className="px-4 pt-4">
                  <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 space-y-1">
                    <p className="font-body text-sm font-semibold text-text-primary">
                      {t(order.cancelledBy === 'owner'
                        ? 'myOrders.cancelledByOwner'
                        : order.cancelledBy === 'customer'
                          ? 'myOrders.cancelledByCustomer'
                          : 'myOrders.cancelledBySystem')}
                    </p>
                    {order.cancelReason && (
                      <p className="font-body text-xs text-text-secondary leading-relaxed">
                        {t('myOrders.cancelReasonLabel')}: {order.cancelReason}
                      </p>
                    )}
                    <p className="font-body text-xs text-text-tertiary">
                      {order.paid
                        ? t('myOrders.cancelRefundNote')
                        : t('myOrders.cancelNoCharge')}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="px-4 pt-4">
                  <div className="flex items-center">
                    {steps.map((step, i) => {
                      const isDone = i <= currentIdx && currentIdx >= 0
                      const isCurrent = i === currentIdx
                      return (
                        <div key={step.key} className="flex-1 flex flex-col items-center relative">
                          {i > 0 && (
                            <div
                              className={`absolute top-2.5 right-1/2 w-full h-0.5 ${
                                i <= currentIdx ? 'bg-matcha' : 'bg-gray-200'
                              }`}
                            />
                          )}
                          <div
                            className={`relative z-10 h-5 w-5 rounded-full flex items-center justify-center ${
                              isDone ? 'bg-matcha' : 'bg-gray-200'
                            } ${isCurrent ? 'ring-4 ring-matcha/20' : ''}`}
                          >
                            {isDone && <div className="h-2 w-2 rounded-full bg-white" />}
                          </div>
                          <span className={`mt-1.5 font-body text-[10px] text-center ${
                            isCurrent ? 'text-matcha font-semibold' : 'text-text-tertiary'
                          }`}>
                            {step.label}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                  {/* Approval countdown while pre-capture. Reassures the
                   * customer that the store has a deadline to respond
                   * (10 min) and will auto-cancel with full auth release
                   * if they don't. */}
                  {order.status === 'received' && !order.captured && order.createdAt && (
                    <div className="mt-3 text-center">
                      <DeliveryApprovalCountdown createdAtIso={order.createdAt} audience="customer" />
                    </div>
                  )}
                </div>
              )}

              {/* Items */}
              <div className="px-4 pt-3 pb-2">
                <p className="font-jp text-sm text-text-primary">
                  {order.items.map((i) => `${itemNameOneLine(i)} × ${i.quantity}`).join('、')}
                </p>
                {order.deliveryAddress && (
                  <p className="mt-1 font-body text-xs text-text-tertiary">
                    {t('myOrders.deliveryTo')}: <span className="text-text-primary">{order.deliveryAddress}</span>
                  </p>
                )}
              </div>

              <div className="px-4 pb-3 flex items-center justify-between gap-2">
                <span className="font-body text-xs text-text-secondary">
                  {t('myOrders.total')}: <span className="font-display font-bold text-text-primary">¥{order.totalAmount.toLocaleString()}</span>
                </span>
                {order.missingReported && (
                  <span className="rounded-full bg-red-100 text-red-700 px-2 py-0.5 text-[10px] font-body font-bold">
                    {t('myOrders.reportedBadge')}
                  </span>
                )}
              </div>

              {(canCancel || canReportMissing) && (
                <div className="px-4 pb-4 flex items-center gap-2">
                  {canCancel && (
                    <button
                      onClick={() => setCancelTarget(order)}
                      className="flex-1 h-10 rounded-lg border border-gray-200 text-text-secondary font-body text-xs tap-effect flex items-center justify-center gap-1"
                    >
                      <X size={12} />
                      {t('myOrders.cancelDeliveryOrder')}
                    </button>
                  )}
                  {canReportMissing && (
                    <button
                      onClick={() => setReportTarget(order)}
                      className="flex-1 h-10 rounded-lg bg-red-50 border border-red-200 text-red-700 font-body text-xs tap-effect flex items-center justify-center gap-1 font-semibold"
                    >
                      <AlertTriangle size={12} />
                      {t('myOrders.reportMissing')}
                    </button>
                  )}
                </div>
              )}
              {/* #338 chat: only on delivery orders (dine-in staff is right
                 * there at the table). Visible even after delivery so the
                 * customer can review history, but input is locked by the
                 * sheet when the order is terminal. */}
              {order.orderType === 'delivery' && (
                <div className="px-4 pb-4">
                  <button
                    onClick={() => setChatOrderId(order.id)}
                    className="w-full h-10 rounded-lg border border-gray-200 text-text-secondary font-body text-xs tap-effect flex items-center justify-center gap-2"
                  >
                    <MessageCircle size={12} />
                    <span>{t('chat.openCustomer')}</span>
                    {(order.unreadCountForCustomer ?? 0) > 0 && (
                      <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold">
                        {order.unreadCountForCustomer}
                      </span>
                    )}
                  </button>
                </div>
              )}
            </div>
          )
        })}

        {openBills.map((bill) => {
          // Latest non-empty tableNumber across the open bill — matches the
          // owner-side rule in StoreOrders so the customer sees the same
          // table label the store does.
          const billTable = bill.orders
            .slice()
            .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
            .find((o) => !!o.tableNumber)?.tableNumber
          // #1535 卓に着席中はこのカードを「卓の伝票」にする — 自分・友達・
          // 店員入力を 1 つのリストに束ね、状態グループで分ける (伝票=卓単位)。
          const isTableSlip = tableOrders.length > 0 && bill.storeId === tableOrders[0].storeId
          const slipTable = isTableSlip ? tableOrders[0].tableNumber : billTable
          // 自分の別テーブルの未払い (席移動・閉め忘れ)。卓の伝票には入らないが
          // 「自分の分」会計には含まれるため、別見出しで同カードに残す。
          const ownElsewhere = isTableSlip
            ? bill.orders.filter((o) => o.tableNumber !== slipTable)
            : []
          const slipRows = (isTableSlip
            ? [
                ...bill.orders
                  .filter((o) => o.tableNumber === slipTable)
                  .map((o) => ({ order: o, mine: true })),
                ...tableOrders.map((o) => ({ order: o, mine: false })),
              ]
            : bill.orders.map((o) => ({ order: o, mine: true }))
          ).sort((a, b) => (a.order.createdAt < b.order.createdAt ? -1 : 1))
          const renderRow = (
            { order, mine }: { order: Order; mine: boolean },
            opts?: { showStatusBadge?: boolean },
          ) => {
            // "届いていない" gate: served + unpaid + not already reported.
            // Server enforces the same rule; frontend just hides the
            // action when it'd 400. 他者の注文には操作を出さない。
            const canReportMissing =
              mine && order.status === 'served' && !order.paid && !order.missingReported
            return (
              <div
                key={order.id}
                className={`px-4 py-3 ${mine && order.missingReported ? 'bg-red-50/40' : ''}`}
              >
                <div className="flex items-center justify-between flex-wrap gap-1">
                  <div className="flex items-center gap-1.5 min-w-0">
                    {/* #1535 卓伝票では誰の注文かのタグ (自分/友達/店員が入力) */}
                    {isTableSlip && (
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-body font-bold shrink-0 ${
                        mine
                          ? 'bg-yokoso-red/10 text-yokoso-red'
                          : isStaffEnteredOrder(order)
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-blue-100 text-blue-700'
                      }`}>
                        {t(mine
                          ? 'checkout.byMe'
                          : isStaffEnteredOrder(order) ? 'myOrders.staffPlaced' : 'myOrders.byFriend')}
                      </span>
                    )}
                    <p className="font-display text-xs font-semibold text-text-secondary">
                      #{displayOrderNumber(order)}
                    </p>
                    {mine && order.missingReported && (
                      <span className="rounded-full bg-red-100 text-red-700 px-2 py-0.5 text-[10px] font-body font-bold">
                        {t('myOrders.reportedBadge')}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {/* #1535 注文時刻 — 「頼んだ/頼んでない」で揉めないための記録。
                       * 卓伝票では状態はグループ見出しが持つので、行には時刻を出す */}
                    {(!isTableSlip || opts?.showStatusBadge) && statusBadge(order)}
                    {isTableSlip && formatOrderTime(order.createdAt) && (
                      <span className="font-body text-[11px] text-text-tertiary">
                        {formatOrderTime(order.createdAt)}
                      </span>
                    )}
                  </div>
                </div>
                {/* #1539 (REQ-24) 品目は省略せず 1 品 1 行で全文表示 — 店員入力や
                  * 友達の注文が卓の伝票に載る運用では「何が載っているか読めない」が
                  * 不信・店員への確認手間に直結する。店側カードと同じ形式 */}
                <ul className="mt-1 space-y-0.5">
                  {order.items.map((item, idx) => (
                    <li key={`${item.menuItemId || item.name}-${idx}`}>
                      <div className="flex items-baseline justify-between gap-2">
                        {/* #1857 客の言語が主・日本語が従 (店員に見せて確認できる形) */}
                        <span className="min-w-0 flex-1">
                          <span className="block font-body text-sm text-text-primary">
                            {itemNameLines(item).primary}
                          </span>
                          {itemNameLines(item).secondary && (
                            <span className="block font-jp text-xs text-text-tertiary">
                              {itemNameLines(item).secondary}
                            </span>
                          )}
                          <VariationLine item={item} />
                        </span>
                        <span className="font-body text-xs text-text-secondary shrink-0">
                          × {item.quantity}
                        </span>
                      </div>
                      {/* #1644 コース明細の品目 (予約時スナップショット)。
                          「何を注文していて何が届くか」を注文履歴でも読めるようにする */}
                      {item.dishList && (
                        <div className="mt-0.5">
                          <DishListPanel dishList={item.dishList} size="xs" />
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
                <div className="mt-1.5 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-3">
                    {/* #968 再注文: 同じ品をもう一度カートへ (自分の注文のみ)。
                      * #2039 時間料金の伝票には出さない — 時間制プランは客が頼めず
                      * (時計の起点は店員の操作)、カートに戻してもサーバが必ず断る。 */}
                    {mine && !order.items.some((i) => i.timeCharge) && (
                      <button
                        onClick={() => handleReorder(bill, order)}
                        className="font-body text-[11px] text-yokoso-red font-semibold tap-effect inline-flex items-center gap-1"
                      >
                        <RotateCcw size={12} />
                        {t('myOrders.reorder')}
                      </button>
                    )}
                    {/* "届いていない" は escape hatch なので控えめなテキストリンク */}
                    {canReportMissing && (
                      <button
                        onClick={() => setReportTarget(order)}
                        className="font-body text-[11px] text-text-tertiary underline underline-offset-2 tap-effect hover:text-red-600"
                      >
                        {t('myOrders.reportMissing')}
                      </button>
                    )}
                  </div>
                  <p className="font-display text-sm font-bold text-gold">
                    ¥{order.totalAmount.toLocaleString()}
                  </p>
                </div>
              </div>
            )
          }
          return (
          <div key={bill.storeId} className="rounded-2xl bg-white shadow-card overflow-hidden">
            {/* Bill header */}
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <p className="font-display text-sm font-semibold text-text-primary">
                  {bill.storeName}
                </p>
                {slipTable && (
                  <span className="rounded-full bg-blue-100 text-blue-700 px-2 py-0.5 text-[10px] font-body font-bold">
                    {t('storeDetail.tableBadge', { table: slipTable })}
                  </span>
                )}
              </div>
              <p className="font-body text-xs text-text-tertiary mt-0.5">
                {t('myOrders.orderCount', {
                  count: isTableSlip ? slipRows.length + ownElsewhere.length : bill.orders.length,
                })}
              </p>
            </div>

            {/* Line items — 卓伝票は状態グループ (受付→調理中→提供済) で分ける */}
            {isTableSlip ? (
              <div className="divide-y divide-gray-100">
                {SLIP_STATUS_ORDER
                  .map((s) => ({
                    status: s,
                    rows: slipRows.filter((r) =>
                      (SLIP_STATUS_ORDER.includes(r.order.status) ? r.order.status : 'received') === s),
                  }))
                  .filter((g) => g.rows.length > 0)
                  .map((g) => (
                    <div key={g.status}>
                      <div className="px-4 pt-2.5 pb-1 flex items-center gap-2 bg-gray-50/50">
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-body font-semibold shrink-0 ${SLIP_STATUS_COLOR[g.status] || 'bg-gray-100 text-text-secondary'}`}>
                          {t(`myOrders.status.${g.status}`)} ({g.rows.length})
                        </span>
                        <span className="h-px flex-1 bg-gray-100" />
                      </div>
                      <div className="divide-y divide-gray-50">
                        {g.rows.map((r) => renderRow(r))}
                      </div>
                    </div>
                  ))}
                {ownElsewhere.length > 0 && (
                  <div>
                    <div className="px-4 pt-2.5 pb-1 flex items-center gap-2 bg-gray-50/50">
                      <span className="font-body text-[11px] font-semibold text-text-secondary shrink-0">
                        {t('myOrders.ownOtherTableTitle')}
                      </span>
                      <span className="h-px flex-1 bg-gray-100" />
                    </div>
                    <div className="divide-y divide-gray-50">
                      {ownElsewhere.map((o) =>
                        renderRow({ order: o, mine: true }, { showStatusBadge: true }))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {slipRows.map((r) => renderRow(r))}
              </div>
            )}

            {/* Bill total + actions */}
            <div className="px-4 py-3 border-t border-gray-100">
              {/* #1535 卓伝票ではテーブル全員分を主の合計に、自分の分は補助表示 —
                 * ラベル無しの金額は卓の合計と誤読され会計時に揉める */}
              {isTableSlip && tableAllTotal > 0 && (
                <div className="flex items-center justify-between mb-1">
                  <span className="font-body text-sm text-text-secondary">
                    {t('myOrders.tableTotalLabel')}
                  </span>
                  <span className="font-display text-xl font-bold text-text-primary">
                    ¥{tableAllTotal.toLocaleString()}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between mb-3">
                <span className="font-body text-sm text-text-secondary">
                  {t(isTableSlip ? 'myOrders.billTotalMine' : 'myOrders.billTotal')}
                </span>
                <span className={`font-display font-bold text-text-primary ${isTableSlip ? 'text-base' : 'text-xl'}`}>
                  ¥{bill.total.toLocaleString()}
                </span>
              </div>
              {/* #942 現金会計待ち: customer chose cash and is waiting for the
               * store to confirm receipt at the register. Replace the pay
               * button with a guidance banner — the bill flips to paid via
               * the Firestore listener once the owner approves. */}
              {bill.orders.some((o) => o.cashPending && !o.paid) ? (
                <CashPendingBanner storeId={bill.storeId} />
              ) : (
                <>
                  {/* #947 未承認注文がある間は会計できない（店員の承認後に payable）。
                   * 表示総額と請求額のズレを防ぎつつ、追加注文は継続可。 */}
                  {bill.orders.some((o) => o.status === 'pending_approval') && (
                    <p className="mb-2 rounded-lg bg-purple-50 border border-purple-100 px-3 py-2 font-body text-xs text-purple-800 leading-relaxed">
                      {t('myOrders.awaitingApprovalNote')}
                    </p>
                  )}
                  <div className="flex gap-2">
                    {/* #949: navigate to the clean store URL (no table number).
                     * #947's server-derived seat keeps the table context. */}
                    <button
                      onClick={() => navigate(`/stores/${bill.storeId}`)}
                      className="flex-1 h-12 border border-gray-200 text-text-primary rounded-xl font-display font-semibold text-sm flex items-center justify-center gap-2 tap-effect"
                    >
                      <Plus size={16} />
                      {t('myOrders.addMore')}
                    </button>
                    <button
                      onClick={() => setCheckoutTarget(bill)}
                      disabled={bill.orders.some((o) => o.status === 'pending_approval')}
                      className="flex-1 h-12 bg-yokoso-red text-white rounded-xl font-display font-semibold text-sm flex items-center justify-center gap-2 tap-effect active:scale-[0.98] transition-transform disabled:opacity-40 disabled:active:scale-100"
                    >
                      <Receipt size={16} />
                      {t('myOrders.payThisBill')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )})}

        {/* Link to full history (transactions) for closed sessions */}
        {totalOrderCount > 0 && (
          <button
            onClick={() => navigate('/history')}
            className="w-full text-center text-xs text-text-tertiary font-body underline py-2"
          >
            {t('myOrders.seeHistory')}
          </button>
        )}
      </div>

      {liveCheckoutTarget && (
        <CheckoutSheet
          open={!!liveCheckoutTarget}
          storeId={liveCheckoutTarget.bill.storeId}
          storeName={liveCheckoutTarget.bill.storeName}
          unpaidOrders={liveCheckoutTarget.bill.orders}
          billSettled={liveCheckoutTarget.settled}
          onClose={() => setCheckoutTarget(null)}
          // Default onPaid navigates to /payment/complete (receipt + review/share).
        />
      )}

      <OrderChatSheet
        open={!!chatOrderId}
        orderId={chatOrderId}
        role="customer"
        closedForWrite={chatOrder?.status === 'delivered' || chatOrder?.status === 'cancelled'}
        userId={userId || null}
        storeNameForCustomer={chatOrder
          ? stores.find((s) => s.id === chatOrder.storeId)?.name
          : undefined}
        onClose={() => setChatOrderId(null)}
      />

      {reportTarget && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 px-6"
          onClick={() => !reporting && setReportTarget(null)}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
              <AlertTriangle size={20} className="text-red-500" />
            </div>
            <h3 className="mt-4 font-display text-lg font-bold text-text-primary">
              {t('myOrders.reportConfirmTitle')}
            </h3>
            <p className="mt-2 font-body text-sm text-text-secondary leading-relaxed">
              {t('myOrders.reportConfirmDesc')}
            </p>
            <p className="mt-3 rounded-lg bg-gray-50 px-3 py-2 font-jp text-sm text-text-primary">
              #{displayOrderNumber(reportTarget)} —{' '}
              {reportTarget.items.map((i) => `${itemNameOneLine(i)} × ${i.quantity}`).join('、')}
            </p>
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setReportTarget(null)}
                disabled={reporting}
                className="tap-effect flex-1 rounded-xl border border-gray-200 bg-white py-3 font-display text-sm font-semibold text-text-primary"
              >
                {t('myOrders.reportCancel')}
              </button>
              <button
                onClick={submitMissingReport}
                disabled={reporting}
                className="tap-effect flex-1 rounded-xl bg-red-500 py-3 font-display text-sm font-semibold text-white disabled:opacity-50"
              >
                {reporting ? '...' : t('myOrders.reportConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* #284 customer cancel confirmation for delivery orders */}
      {cancelTarget && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 px-6"
          onClick={() => !cancelling && setCancelTarget(null)}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
              <AlertTriangle size={20} className="text-amber-500" />
            </div>
            <h3 className="mt-4 font-display text-lg font-bold text-text-primary">
              {t('myOrders.cancelConfirmTitle')}
            </h3>
            <p className="mt-2 font-body text-sm text-text-secondary leading-relaxed">
              {t('myOrders.cancelConfirmDesc')}
            </p>
            <p className="mt-3 rounded-lg bg-gray-50 px-3 py-2 font-jp text-sm text-text-primary">
              #{displayOrderNumber(cancelTarget)} — ¥{cancelTarget.totalAmount.toLocaleString()}
            </p>
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setCancelTarget(null)}
                disabled={cancelling}
                className="tap-effect flex-1 rounded-xl border border-gray-200 bg-white py-3 font-display text-sm font-semibold text-text-primary"
              >
                {t('myOrders.cancelKeep')}
              </button>
              <button
                onClick={submitCancel}
                disabled={cancelling}
                className="tap-effect flex-1 rounded-xl bg-red-500 py-3 font-display text-sm font-semibold text-white disabled:opacity-50"
              >
                {cancelling ? '...' : t('myOrders.cancelConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * #942/#1178 現金会計待ちバナー。店の会計スタイルで案内を出し分ける:
 * - スタッフが伝票を持参する店 → 「お席でお待ちください」
 * - S700 導入店 → 「レジで現金・カードが使えます」
 * - それ以外 → 従来の現金レジ案内
 * store 設定はシート表示のたびに直接取得 (useCheckoutStyle — 起動時
 * スナップショットの stores リストだと設定変更が反映されないため)。
 *
 * #2344 **カードが使えると言ってよいのは payAtRegister の店だけ。** 伝票持参の店の
 * 文言はカードに触れていたので、現金のみの店で「このお店は現金のみです」と
 * 「クレジットカードもご利用いただけます」が同じ画面に並んでいた。
 */
function CashPendingBanner({ storeId }: { storeId: string }) {
  const { t } = useI18n()
  const info = useCheckoutStyle(storeId)
  const title = info?.staffBrings
    ? t('checkout.cashGuideTitleStaffBrings')
    : t('myOrders.cashPendingTitle')
  const note = info?.staffBrings
    ? t(info?.payAtRegister
      ? 'checkout.cashGuideDescStaffBrings'
      : 'checkout.cashGuideDescStaffBringsCash')
    : info?.payAtRegister
      ? t('checkout.cashGuideDescRegister')
      : t('myOrders.cashPendingNote')
  return (
    <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 flex items-center gap-3">
      <Banknote size={18} className="text-amber-600 shrink-0" />
      <div>
        <p className="font-display text-sm font-semibold text-amber-800">{title}</p>
        <p className="font-body text-xs text-amber-700 mt-0.5 leading-relaxed">{note}</p>
      </div>
    </div>
  )
}
